export interface JobMatchConfig {
  enabled: boolean;
  maxFileSize: number;
  supportedFormats: string[];
  analysisTimeout: number;
  resultsTimeout: number;
  resultsRetentionDays: number;
  minDescriptionLength: number;
  maxDescriptionLength: number;
  maxRetries: number;
  retryDelay: number;
  debugMode: boolean;
}

const parseBoolean = (value: string | undefined, defaultValue: boolean): boolean => {
  if (!value) return defaultValue;
  return value.toLowerCase() === 'true' || value === '1';
};

const parseInt = (value: string | undefined, defaultValue: number): number => {
  if (!value) return defaultValue;
  const parsed = Number(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

const parseArray = (value: string | undefined, defaultValue: string[]): string[] => {
  if (!value) return defaultValue;
  return value.split(',').map(v => v.trim()).filter(v => v.length > 0);
};

const getConfig = (): JobMatchConfig => {
  const config: JobMatchConfig = {
    enabled: parseBoolean(import.meta.env.VITE_JOB_MATCH_AI_ENABLED, true),
    maxFileSize: parseInt(import.meta.env.VITE_JOB_MATCH_MAX_FILE_SIZE, 10 * 1024 * 1024),
    supportedFormats: parseArray(import.meta.env.VITE_JOB_MATCH_SUPPORTED_FORMATS, ['pdf', 'docx', 'doc', 'txt']),
    analysisTimeout: parseInt(import.meta.env.VITE_JOB_MATCH_ANALYSIS_TIMEOUT, 300000),
    resultsTimeout: parseInt(import.meta.env.VITE_JOB_MATCH_RESULTS_TIMEOUT, 30000),
    resultsRetentionDays: parseInt(import.meta.env.VITE_JOB_MATCH_RESULTS_RETENTION_DAYS, 365),
    minDescriptionLength: parseInt(import.meta.env.VITE_JOB_MATCH_MIN_DESCRIPTION_LENGTH, 50),
    maxDescriptionLength: parseInt(import.meta.env.VITE_JOB_MATCH_MAX_DESCRIPTION_LENGTH, 50000),
    maxRetries: parseInt(import.meta.env.VITE_JOB_MATCH_MAX_RETRIES, 3),
    retryDelay: parseInt(import.meta.env.VITE_JOB_MATCH_RETRY_DELAY, 500),
    debugMode: parseBoolean(import.meta.env.VITE_JOB_MATCH_DEBUG_MODE, false)
  };

  return config;
};

const validateConfig = (config: JobMatchConfig): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (config.maxFileSize <= 0) {
    errors.push('maxFileSize must be greater than 0');
  }

  if (config.maxFileSize > 100 * 1024 * 1024) {
    errors.push('maxFileSize should not exceed 100MB for practical reasons');
  }

  if (config.supportedFormats.length === 0) {
    errors.push('supportedFormats must include at least one format');
  }

  if (config.analysisTimeout < 10000) {
    errors.push('analysisTimeout should be at least 10 seconds');
  }

  if (config.analysisTimeout > 600000) {
    errors.push('analysisTimeout should not exceed 10 minutes');
  }

  if (config.resultsTimeout < 5000) {
    errors.push('resultsTimeout should be at least 5 seconds');
  }

  if (config.minDescriptionLength < 10) {
    errors.push('minDescriptionLength should be at least 10 characters');
  }

  if (config.maxDescriptionLength < config.minDescriptionLength) {
    errors.push('maxDescriptionLength must be greater than minDescriptionLength');
  }

  if (config.maxDescriptionLength > 100000) {
    errors.push('maxDescriptionLength should not exceed 100,000 characters');
  }

  if (config.maxRetries < 0) {
    errors.push('maxRetries must be 0 or greater');
  }

  if (config.maxRetries > 10) {
    errors.push('maxRetries should not exceed 10');
  }

  if (config.retryDelay < 0) {
    errors.push('retryDelay must be 0 or greater');
  }

  if (config.resultsRetentionDays < 1) {
    errors.push('resultsRetentionDays must be at least 1 day');
  }

  if (config.resultsRetentionDays > 3650) {
    errors.push('resultsRetentionDays should not exceed 10 years (3650 days)');
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

export const jobMatchConfig = getConfig();

const validation = validateConfig(jobMatchConfig);
if (!validation.valid) {
  console.error('[Job Match Config] Configuration validation failed:', validation.errors);
  if (jobMatchConfig.debugMode) {
    console.error('[Job Match Config] Invalid configuration:', jobMatchConfig);
  }
}

if (jobMatchConfig.debugMode) {
  console.log('[Job Match Config] Loaded configuration:', jobMatchConfig);
}

export const getJobMatchConfig = (): Readonly<JobMatchConfig> => {
  return Object.freeze({ ...jobMatchConfig });
};

export const isJobMatchEnabled = (): boolean => {
  return jobMatchConfig.enabled;
};

export const getMaxFileSize = (): number => {
  return jobMatchConfig.maxFileSize;
};

export const getSupportedFormats = (): readonly string[] => {
  return Object.freeze([...jobMatchConfig.supportedFormats]);
};

export const getAnalysisTimeout = (): number => {
  return jobMatchConfig.analysisTimeout;
};

export const getResultsTimeout = (): number => {
  return jobMatchConfig.resultsTimeout;
};

export const getRetentionDays = (): number => {
  return jobMatchConfig.resultsRetentionDays;
};

export const getDescriptionLimits = (): { min: number; max: number } => {
  return {
    min: jobMatchConfig.minDescriptionLength,
    max: jobMatchConfig.maxDescriptionLength
  };
};

export const getRetryConfig = (): { maxRetries: number; retryDelay: number } => {
  return {
    maxRetries: jobMatchConfig.maxRetries,
    retryDelay: jobMatchConfig.retryDelay
  };
};

export const isDebugMode = (): boolean => {
  return jobMatchConfig.debugMode;
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

export const getFormattedMaxFileSize = (): string => {
  return formatFileSize(jobMatchConfig.maxFileSize);
};

export const isSupportedFormat = (format: string): boolean => {
  const normalizedFormat = format.toLowerCase().replace('.', '');
  return jobMatchConfig.supportedFormats.some(
    f => f.toLowerCase() === normalizedFormat
  );
};

export const getSupportedFormatsString = (): string => {
  return jobMatchConfig.supportedFormats.map(f => `.${f}`).join(', ');
};

export const getConfigSummary = (): string => {
  return `
Job Match AI Configuration:
- Feature Enabled: ${jobMatchConfig.enabled}
- Max File Size: ${getFormattedMaxFileSize()}
- Supported Formats: ${getSupportedFormatsString()}
- Analysis Timeout: ${jobMatchConfig.analysisTimeout}ms
- Results Timeout: ${jobMatchConfig.resultsTimeout}ms
- Retention Period: ${jobMatchConfig.resultsRetentionDays} days
- Description Length: ${jobMatchConfig.minDescriptionLength}-${jobMatchConfig.maxDescriptionLength} chars
- Max Retries: ${jobMatchConfig.maxRetries}
- Retry Delay: ${jobMatchConfig.retryDelay}ms
- Debug Mode: ${jobMatchConfig.debugMode}
  `.trim();
};
